import classNames from "classnames";
import { FC, SyntheticEvent, useEffect, useRef, useState } from "react";
import EmbedConsent from "rivals/components/shared/EmbedConsent";
import {
  MOBILE_FOOTER_HEIGHT,
  MOBILE_HEADER_HEIGHT,
  VIDEO_PROVIDERS
} from "rivals/shared/constants";
import { useScript } from "rivals/shared/hooks/useScript";
import { useIsMobileWidth } from "rivals/shared/utils/mediaQuery";
import { setData } from "rivals/shared/utils/qa";
import { useElementOnScreen } from "rivals/shared/utils/useElementOnScreen";
import styles from "./VideoPlayer.module.scss";
import strings from "./strings";
import { Props } from "./types";

const srcUrl = (remoteId: string, source: string): string => {
  if (source === VIDEO_PROVIDERS.yahoo_sports.source) {
    return strings.yahooUrl(remoteId);
  } else if (source === VIDEO_PROVIDERS.vimeo.source) {
    return strings.vimeoUrl(remoteId);
  } else {
    return strings.defaultUrl(remoteId, source);
  }
};
const MOBILE_AUTOPLAY_ADDITIONAL_MARGIN = 72;
const MOBILE_AUTO_PLAY_TOP_MARGIN =
  MOBILE_HEADER_HEIGHT + MOBILE_AUTOPLAY_ADDITIONAL_MARGIN;
const MOBILE_AUTO_PLAY_BOTTOM_MARGIN =
  MOBILE_FOOTER_HEIGHT + MOBILE_AUTOPLAY_ADDITIONAL_MARGIN;

const PREVIEW_DURATION_SECONDS = 60;
export const FEATURED_MEDIA_ROOT_MARGIN = `${-MOBILE_AUTO_PLAY_TOP_MARGIN}px 0px ${-MOBILE_AUTO_PLAY_BOTTOM_MARGIN}px 0px`;

const VideoPlayer: FC<Props> = ({
  aolCompanyId,
  aolPlayerId,
  autoplay,
  autoplayDuration = PREVIEW_DURATION_SECONDS,
  autoplayOnHover = true,
  autoplayOnMobile = false,
  className,
  hasEmbedConsent,
  onVisibilityChange,
  srcId,
  philter,
  promptEmbedConsent = true,
  srcProvider,
  observerOptions,
  ...props
}) => {
  const isMobile = useIsMobileWidth();
  const [isHovering, setIsHovering] = useState<boolean>();
  const videoPlayerRef = useRef<HTMLIFrameElement | null>(null);
  const videoElementRef = useRef<HTMLDivElement | null>(null);
  const isAol = srcProvider === VIDEO_PROVIDERS.aol.source;
  const isHudl = srcProvider === VIDEO_PROVIDERS.hudl.source;
  const isYoutube = srcProvider === VIDEO_PROVIDERS.youtube.source;
  const canShowAol = isAol && aolPlayerId && aolCompanyId;
  const { isVisible } = useElementOnScreen({
    options: {
      root: null,
      rootMargin: FEATURED_MEDIA_ROOT_MARGIN,
      threshold: 0,
      ...observerOptions
    },
    target: isMobile ? videoPlayerRef?.current : null
  });

  useScript({
    shouldAppend: !!(canShowAol && hasEmbedConsent),
    src: `//delivery.vidible.tv/jsonp/pid=${aolPlayerId}/vid=${srcId}/${aolCompanyId}.js`
  });

  const onMouseOver = (event: SyntheticEvent<HTMLDivElement>): void => {
    setIsHovering(true);

    if (props.onMouseOver) {
      props.onMouseOver(event);
    }
  };

  const onMouseOut = (event: SyntheticEvent<HTMLDivElement>): void => {
    setIsHovering(false);

    if (props.onMouseOut) {
      props.onMouseOut(event);
    }
  };

  // Handle video autoplay on featured media page
  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(isVisible);
    }
  }, [isVisible, onVisibilityChange]);

  const resolveNonAolSrc = (): string => {
    const url = srcUrl(srcId, srcProvider);
    const shouldAutoplayOnHover = autoplayOnHover && isHovering;
    const shouldAutoplayOnMobile =
      autoplay && isMobile && (isVisible || autoplayOnMobile);
    const shouldAutoplayOnDesktop = autoplay && !isMobile;

    /*
     Autoplay the video when hovering, autoplay is true for desktop or
     on mobile when autoplay is true and the video is visible or an autoplay mobile override is set
     */
    const shouldAutoplay =
      shouldAutoplayOnHover ||
      shouldAutoplayOnDesktop ||
      shouldAutoplayOnMobile;

    if (!shouldAutoplay) {
      return url;
    }

    const firstDelimiter = url.includes("?") ? "&" : "?";
    return `${url}${firstDelimiter}autoplay=true&mute=true&end=${autoplayDuration}`;
  };

  if (!hasEmbedConsent) {
    if (promptEmbedConsent) {
      return <EmbedConsent />;
    } else {
      return null;
    }
  }

  if (!canShowAol && !srcId) {
    return null;
  }

  const videoElement = (): React.JSX.Element | null => {
    if (canShowAol) {
      return (
        <div
          className={`vdb_${aolPlayerId}${aolCompanyId}`}
          ref={videoPlayerRef}
          {...setData(philter, "aol")}
        />
      );
    } else {
      return (
        <iframe
          allowFullScreen
          className={classNames(isYoutube && styles.youtubeIframe)}
          ref={videoPlayerRef}
          src={resolveNonAolSrc()}
          title="Embedded Video Player"
          {...setData(philter, "iframe")}
        />
      );
    }
  };

  return (
    <div
      className={classNames(
        styles.videoPlayer,
        className,
        isAol && styles.aolVideoWrapper,
        isHudl && styles.hudlVideoWrapper
      )}
      onBlur={onMouseOut}
      onFocus={onMouseOver}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      ref={videoElementRef}
      role="button"
      {...setData(philter, "container")}
      tabIndex={0}
    >
      {videoElement()}
    </div>
  );
};

export default VideoPlayer;
